/**
 * voicemailActions.js
 * Talkroute
 *
 * Created by Vel Genov on 2/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import moment from "moment";

import uiString from "@constants/uiString";
import apiConstants from "@constants/apiConstants";

import {
  createWelcomeVoicemail,
  VoicemailData,
  voicemailsJSONParser,
  WELCOME_VOICEMAIL,
} from "@services/dataClasses/VoicemailData";
import { getTopRelatedCalls } from "@services/dataClasses/CallData";
import apiServiceInstance from "@services/TalkrouteAPIService";

import FilePathHelper from "@helpers/filePathHelper";
import validateArray from "@helpers/arrayValidator";

import { appConfig } from "../index";
import { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import { IUserMetadata } from "@models/LocalStorageItems.models";
import { handleAuthorizationErrors } from "@helpers/thunkHelpers";
import { ErrorStatusCode } from "@helpers/hooks/useService.hook";
import { RootState } from "@helpers/store/store";
import { mergeContactsToVoicemailList } from "@reducers/contactsReducer";
import { addContactNameToVoicemail } from "@helpers/functions/contacts";

/**
 * Constants for the different redux action types
 */
export const FETCH_VOICEMAILS_BEGIN = "FETCH_VOICEMAILS_BEGIN";
export const FETCH_VOICEMAILS_SUCCESS = "FETCH_VOICEMAILS_SUCCESS";
export const FETCH_VOICEMAILS_FAILURE = "FETCH_VOICEMAILS_FAILURE";
export const FETCH_VOICEMAILBOXES_BEGIN = "FETCH_VOICEMAILBOXES_BEGIN";
export const FETCH_VOICEMAILBOXES_SUCCESS = "FETCH_VOICEMAILBOXES_SUCCESS";
export const FETCH_VOICEMAILBOXES_FAILURE = "FETCH_VOICEMAILBOXES_FAILURE";
export const PLAY_VOICEMAIL = "PLAY_VOICEMAIL";
export const APPLY_VOICEMAIL_FILTER = "APPLY_VOICEMAIL_FILTER";
export const SHOW_VOICEMAIL_DETAILS = "SHOW_VOICEMAIL_DETAILS";
export const HIDE_VOICEMAIL_DETAILS = "HIDE_VOICEMAIL_DETAILS";
export const DELETE_VOICEMAIL_SUCCESS = "DELETE_VOICEMAIL_SUCCESS";
export const UPDATE_READ_STATUS = "UPDATE_READ_STATUS";
export const FETCH_VOICEMAIL_FILE_SUCCESS = "FETCH_VOICEMAIL_FILE_SUCCESS";
export const ERROR_PLAYING_VOICEMAIL = "ERROR_PLAYING_VOICEMAIL";
export const FETCH_VOICEMAIL_EMPTY = "FETCH_VOICEMAIL_EMPTY";


/**
 * Gets the voicemails from a web service
 */
export const fetchVoicemails = (background?: boolean, forceUpdate?: boolean) => {
  return async (dispatch, getState) => {
    const {
      lastFetchTime,
      isWelcomeArrived,
      data: existingVoicemails,
    } = getState().voicemails;

    const {
      userId
    } = getState().authentication;
    const currentTimestamp = moment().unix();
    const metadata = window.localStorage.getItem(localStorageItems.metadata);
    let creatingWelcomeVMallowed = true;
    let isMessageRead = false;
    if (metadata) {
      const userData: IUserMetadata = JSON.parse(metadata).find(_userData => _userData.userId === userId);
      creatingWelcomeVMallowed = userData ? !userData.isWelcomeVoicemailDeleted : true;
      isMessageRead = userData ? userData.isWelcomeVoicemailRead : false;
    }
    
    const deltaTimestamp = currentTimestamp - lastFetchTime;

    if (!forceUpdate && deltaTimestamp < apiConstants().timeBeweenFetch.VOICEMAILS) {
      // Fetch only once every timeBetweenFetch (currently 30 seconds)
      return dispatch(fetchVoicemailEmptyAction());
    }
    if (!background) dispatch(fetchVoicemailsBegin());
    // const contactList = getState().contactsReducer.data;

    try {
      const data = await apiServiceInstance.getVoicemails();
      const parsedData = voicemailsJSONParser(data);
      let updatedVoicemails = validateArray(parsedData) ? parsedData : [];
  
      // If the welcome conversation exists, add it back to the new fetch batch
      const welcomeVoicemailIndex = findVoicemailIndexById({
        voicemails: existingVoicemails,
        voicemailId: WELCOME_VOICEMAIL,
      });
      // If this is a new login, add the welcome conversation
      if (!isWelcomeArrived && creatingWelcomeVMallowed) {
        const newWelcomeVoicemail = createWelcomeVoicemail(isMessageRead);
        updatedVoicemails = [newWelcomeVoicemail, ...updatedVoicemails];
      } else if (welcomeVoicemailIndex >= 0) {
        // If the welcome voicemail exists, add it to the data
        if (validateArray(existingVoicemails)) {
          const existingWelcomeVoicemail =
            existingVoicemails[welcomeVoicemailIndex];
  
          updatedVoicemails = [
            existingWelcomeVoicemail,
            ...updatedVoicemails,
          ];
        }
      }

      // if (contactList) {
      //   updatedVoicemails = addContactNameToVoicemail(contactList, updatedVoicemails)
      // } else {
      //   dispatch(mergeContactsToVoicemailList(true));
      // }
      const sortedData = sortVoicemails(updatedVoicemails);
  
      dispatch(
        fetchVoicemailsSuccess({
          data: sortedData,
        })
      );
      return sortedData;
    } catch (error) {
      if ((error as any).originalError && (error as any).originalError.isAxiosError) {
        (error as any).originalError.response?.status === ErrorStatusCode.UNAUTHORIZED &&
        handleAuthorizationErrors(dispatch, getState().authentication)
      }
      // dispatch(fetchVoicemailsFailure(error.errorParsed))
    }
  };
};

/**
 * Gets the lst of voicemail boxes from a web service
 */
export const fetchVoicemailBoxes = () => {
  return (dispatch) => {
    dispatch(fetchVoicemailBoxesBegin());
    return apiServiceInstance
      .getVoicemailBoxes()
      .then((data) => {
        dispatch(mergeContactsToVoicemailList(true));
        dispatch(
          fetchVoicemailBoxesSuccess({
            data,
          })
        );
        return data;
      })
      .catch((error) => dispatch(fetchVoicemailBoxesFailure(error)));
  };
};

/**
 * Fetches the file for a voicemailId
 *
 * @param voicemailId the voidemailId to fetch a file for
 */
export const fetchVoicemailFile = (voicemailId: number) => {
  const filePathAndName = new FilePathHelper().getVoicemailFilePathAndName(
    voicemailId
  );
  if (appConfig.fileStorage?.exists(filePathAndName)) {
    return (dispatch) =>
      dispatch(fetchVoicemailFileSuccess({ data: filePathAndName }));
  }

  return (dispatch) => {
    // Handle the Welcome voicemail
    if (voicemailId === WELCOME_VOICEMAIL) {
      const welcomeVoicemailFilePath = fetchWelcomeVoicemailFile();
      dispatch(fetchVoicemailFileSuccess({ data: welcomeVoicemailFilePath }));
      return welcomeVoicemailFilePath;
    }

    apiServiceInstance
      .getVoicemailFile({ voicemailId })
      .then((data) => {
        dispatch(
          fetchVoicemailFileSuccess({
            data,
          })
        );
        return data;
      })
      .catch((error) => {
        if (error.message === apiConstants().errors.VOICEMAIL_FILE_NOT_FOUND) {
          dispatch(deleteVoicemailSuccess(voicemailId));
        }

        dispatch(fetchVoicemailsFailure(error));
      });
  };
};

const fetchWelcomeVoicemailFile = () => {
  const filePath = new FilePathHelper().getVoicemailFilePath();

  try {
    if (!appConfig.fileStorage?.exists(filePath)) {
      appConfig.fileStorage?.createPath(filePath);
    }

    const welcomeVoicemailPackagePath = uiString.welcomeVoicemail.FILE_PATH;
    const fullPackagePath = new FilePathHelper().getFullFilePath(
      welcomeVoicemailPackagePath
    );

    if (fullPackagePath.startsWith("http")) return fullPackagePath;

    const filePathWithFileName = new FilePathHelper().getVoicemailFilePathAndName(
      WELCOME_VOICEMAIL
    );

    appConfig.fileStorage?.copy(fullPackagePath, filePathWithFileName);
    return filePathWithFileName;
  } catch (error) {
  }
};

/**
 * Plays a voicemail with Id
 *
 * @param voicemailId the Id of the voicemail to play
 */
export const playVoicemailWithId = (voicemailId: number) => ({
  type: PLAY_VOICEMAIL,
  payload: { playVoicemail: voicemailId },
});

/**
 * Call the remote API to delete a voicemail. Then delete it from the local copy
 *
 * @param voicemailId the id of the voicemail to be deleted
 * @returns a fetchVoicemailSuccess action with the updated voicemails
 */
export const deleteVoicemail = (voicemailId: number, afterDelete?: () => any) => {

  return (dispatch) => {
    // Handle the Welcome voicemail
    if (voicemailId === WELCOME_VOICEMAIL) {
      dispatch(deleteVoicemailSuccess(voicemailId));
      afterDelete && afterDelete();
      return voicemailId;
    }

    apiServiceInstance
      .deleteVoicemail(voicemailId)
      .then(() => {
        dispatch(deleteVoicemailSuccess(voicemailId));
        afterDelete && afterDelete();
        return voicemailId;
      })
      .catch((error) => dispatch(fetchVoicemailsFailure(error)));
  };
};

/**
 * Call the remote API to find the passed in voicemail in a list of voicemails and update its read status
 * Update the local copy
 *
 * @param props.voicemailId the voicemail to update
 * @param props.readStatus the updated read status
 * @param afterUpdate callback which runs after the async request has resolved
 * @returns a fetchVoicemailSuccess action with the updated voicemails
 */
export const updateReadStatus = (props, afterUpdate?: () => void) => {
  const { voicemailId, readStatus } = props;

  return (dispatch) => {
    // Handle the Welcome voicemail
    if (voicemailId === WELCOME_VOICEMAIL) {
      dispatch(updateReadStatusSuccess(voicemailId, readStatus));
      return { voicemailId, readStatus };
    }

    apiServiceInstance
      .updateVoicemailReadStatus({ voicemailId, readStatus })
      .then(() => {
        dispatch(updateReadStatusSuccess(voicemailId, readStatus));
        afterUpdate && afterUpdate();
        return { voicemailId, readStatus };
      })
      .catch((error) => dispatch(fetchVoicemailsFailure(error)));
  };
};

/**
 *
 * @param voicemails the array of voicemails to search in
 * @param voicemailId the ID of the voicemail to search for
 *
 * @returns either a found voicemail or undefined
 */
export const findVoicemailIndexById = (props) => {
  const { voicemails, voicemailId } = props;
  if (validateArray(voicemails)) {
    const foundIndex = voicemails.findIndex(
      (voicemailEntry) => voicemailEntry.id === voicemailId
    );

    return foundIndex;
  }

  return undefined;
};

/**
 * An action to apply a filter to the passed in voicemails
 * @param {*} voicemails the voicemails to filter
 * @param {*} filterBy the phone number to filter by
 */
export const applyVoicemailsFilter = (voicemails, filterBy) => {
  const filteredVoicemails = voicemailsFilterHelper(voicemails, filterBy);

  return {
    type: APPLY_VOICEMAIL_FILTER,
    payload: { filteredVoicemails, filterBy },
  };
};

/**
 * Helper function
 * Filters the passed in voicemais by a passed in filter.
 * @param {*} voicemailData An array containing voicemails
 * @param {*} filterBy A phone number to filter by
 */
export const voicemailsFilterHelper = (voicemailData, filterBy) => {
  if (filterBy === uiString.ALL_MAILBOXES) {
    return voicemailData;
  }

  if (validateArray(voicemailData)) {
    // Null or empty check for voicemails
    const filteredVoicemails = voicemailData.filter(
      (voicemailRecord) => voicemailRecord.mailboxId === filterBy
    );
    return filteredVoicemails;
  }

  return voicemailData;
};

/**
 * Count the number of unread voicemails in the passed in parameter
 *
 * @param {*} voicemails the voicemails to check for any unread ones
 * @return the number of unread voicemails
 */
export const countUnreadVoicemails = (voicemails) => {
  const defaultUnreadCount = 0;

  if (validateArray(voicemails)) {
    // Count the number of messageRead === false occurances in each voicemail
    const unreadCount = voicemails.reduce((accumulator, currentVoicemail) => {
      if (currentVoicemail.messageRead === false) {
        return accumulator + 1;
      }

      return accumulator;
    }, defaultUnreadCount);

    return unreadCount;
  }

  return defaultUnreadCount;
};

/**
 * Show details about a voicemail. Also finds up to callHistoryActions/TOP_CALLS_NUMBER related calls in the call history
 * @param {*} callHistory An array containing call history data to look for related calls in
 * @param {*} voicemail The voicemail to show details for
 * @param {*} parent the parent component that we are showing call details about. Usually Voicemail
 */
export const showVoicemailDetails = (callHistory?, voicemail?, parent?) => {
  if (!voicemail) {
    return {
      type: SHOW_VOICEMAIL_DETAILS,
      payload: {
        id: null,
        topRelatedCalls: getTopRelatedCalls(callHistory, voicemail),
        parent,
      },
    };
  }

  const { id } = voicemail;

  return {
    type: SHOW_VOICEMAIL_DETAILS,
    payload: {
      id,
      topRelatedCalls: getTopRelatedCalls(callHistory, voicemail),
      parent,
    },
  };
};

/**
 * Hide details about a voicemail.
 * @param {*} parent the parent component that we are showing voicemail details about. Usually Voicemail
 */
export const hideVoicemailDetails = (parent) => ({
  type: HIDE_VOICEMAIL_DETAILS,
  payload: {
    voicemail: null,
    topRelatedCalls: [],
    parent,
  },
});

/**
 * Helper function
 * Sorts the passed in array of voicemails by date
 * @param {*} data the voicemails to sort
 */
const sortVoicemails = (data: VoicemailData[]) => {
  const sortedData = data;
  if (Array.isArray(sortedData) && sortedData.length > 1) {
    // Sort by date before returning
    sortedData.sort(
      (a, b) =>
        // Turn the strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        b.date.unix() - a.date.unix()
    );
  }

  return sortedData;
};

/**
 * Start fething voicemails action
 */
export const fetchVoicemailsBegin = () => ({
  type: FETCH_VOICEMAILS_BEGIN,
});

/**
 * Successfully fetched voicemails
 * @param {*} voicemails the voicemails array that was fetched
 */
export const fetchVoicemailsSuccess = (voicemails) => ({
  type: FETCH_VOICEMAILS_SUCCESS,
  payload: { voicemails },
});

/**
 * An error occured while fetching voicemails
 * @param {*} error
 */
export const fetchVoicemailsFailure = (error) => ({
  type: FETCH_VOICEMAILS_FAILURE,
  payload: { error },
});

/**
 * Start fetching voicemail boxes action
 */
export const fetchVoicemailBoxesBegin = () => ({
  type: FETCH_VOICEMAILBOXES_BEGIN,
});

/**
 * Successfully fetched voicemail boxes
 * @param {*} voicemails the voicemails array that was fetched
 */
export const fetchVoicemailBoxesSuccess = (voicemailBoxes) => ({
  type: FETCH_VOICEMAILBOXES_SUCCESS,
  payload: { voicemailBoxes },
});

/**
 * An error occured while fetching voicemail boxes
 * @param {*} error
 */
export const fetchVoicemailBoxesFailure = (error) => ({
  type: FETCH_VOICEMAILBOXES_FAILURE,
  payload: { error },
});

/**
 * Successfully deleted a voicemail with ID
 *
 * @param {*} voicemailId the Id of the voicemail that was deleted
 */
export const deleteVoicemailSuccess = (voicemailId) => ({
  type: DELETE_VOICEMAIL_SUCCESS,
  payload: { voicemailId },
});

/**
 * Successfully updated the read status of a voicemail
 *
 * @param {*} voicemailId the Id of the voicemail that was marked
 * @param {*} readStatus the new read status
 */
export const updateReadStatusSuccess = (voicemailId, readStatus) => ({
  type: UPDATE_READ_STATUS,
  payload: { voicemailId, readStatus },
});

/**
 * Successfully fetched a voicemail file
 *
 * @param {*} voicemailFilePath the filepath to the fetched file
 */
export const fetchVoicemailFileSuccess = (voicemailFilePath) => ({
  type: FETCH_VOICEMAIL_FILE_SUCCESS,
  payload: { voicemailFilePath },
});

/**
 * There was an error playing a voicemail
 *
 * @param {*} error the error encountered while playing a voicemail
 */
export const errorPlayingVoicemail = (error) => ({
  type: ERROR_PLAYING_VOICEMAIL,
  payload: { error },
});

/**
 * An empty action. Does not change the state
 */
export const fetchVoicemailEmptyAction = () => ({
  type: FETCH_VOICEMAIL_EMPTY,
});

const voicemailActions = {
  fetchVoicemails,
  fetchVoicemailBoxes,
  fetchVoicemailFile,
  fetchWelcomeVoicemailFile,
  playVoicemailWithId,
  deleteVoicemail,
  updateReadStatus,
  findVoicemailIndexById,
  applyVoicemailsFilter,
  voicemailsFilterHelper,
  countUnreadVoicemails,
  showVoicemailDetails,
  hideVoicemailDetails,
  fetchVoicemailsBegin,
  fetchVoicemailsSuccess,
  fetchVoicemailsFailure,
  fetchVoicemailBoxesBegin,
  fetchVoicemailBoxesSuccess,
  fetchVoicemailBoxesFailure,
  deleteVoicemailSuccess,
  updateReadStatusSuccess,
  fetchVoicemailFileSuccess,
  errorPlayingVoicemail,
  fetchVoicemailEmptyAction,
};

export default voicemailActions;
